/**
 * Nephros date input utility
 *
 * Exports:
 *   parseDateInput(str)     — parses user-typed date → { iso, display } or null
 *   isoToDisplay(isoStr)    — converts ISO → display format for pre-filling forms
 *   isValidDateInput(str)   — convenience boolean validator
 *   DateInputField          — React component: text input + calendar icon trigger
 *
 * Accepted input formats: M/D/YY, M/D/YYYY, M-D-YY, M-D-YYYY (zero-padded ok)
 * Display format: M/D/YYYY (e.g. 5/15/2026, 12/24/2020)
 *
 * Century rule for two-digit years:
 *   00–50 → 2000s (e.g. 26 → 2026)
 *   51–99 → 1900s (e.g. 75 → 1975)
 */

import React, { useRef } from 'react';
import { IconCalendarWeek } from '@tabler/icons-react';

// ---------------------------------------------------------------------------
// Pure utility functions
// ---------------------------------------------------------------------------

export function parseDateInput(str) {
  if (!str || !str.trim()) return null;

  const normalized = str.trim().replace(/-/g, '/');
  const parts = normalized.split('/');
  if (parts.length !== 3) return null;

  const m = parseInt(parts[0], 10);
  const d = parseInt(parts[1], 10);
  let y   = parseInt(parts[2], 10);

  if (isNaN(m) || isNaN(d) || isNaN(y)) return null;

  // Resolve two-digit year
  if (y >= 0 && y <= 50)  y = 2000 + y;
  else if (y <= 99)        y = 1900 + y;

  // Basic range checks
  if (m < 1 || m > 12)        return null;
  if (d < 1 || d > 31)        return null;
  if (y < 1900 || y > 2100)   return null;

  // Validate as real calendar date (catches Feb 30 etc.)
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth()    !== m - 1 ||
    date.getDate()     !== d
  ) return null;

  const iso     = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const display = `${m}/${d}/${y}`;

  return { iso, display };
}

export function isoToDisplay(isoStr) {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${parseInt(y)}`;
}

export function isValidDateInput(str) {
  return parseDateInput(str) !== null;
}

export function todayDisplay() {
  const iso = new Date().toISOString().split('T')[0];
  return isoToDisplay(iso);
}

// ---------------------------------------------------------------------------
// DateInputField — shared React component
//
// Props:
//   value       — display string (M/D/YYYY) — controlled by parent
//   onChange    — fn(displayStr, isoStr | null) — called on every keystroke
//                 isoStr is null when the current value is not yet a valid date
//   placeholder — defaults to 'M/D/YY or M/D/YYYY'
//   error       — error string or null — triggers red border + error text
//   disabled    — boolean
//   onKeyDown   — forwarded to the text input (e.g. for Enter-to-save)
//   inputStyle  — optional additional styles merged onto the input
// ---------------------------------------------------------------------------

export function DateInputField({
  value,
  onChange,
  placeholder = 'M/D/YY or M/D/YYYY',
  error,
  disabled,
  onKeyDown,
  inputStyle,
  autoFocus,
}) {
  const hiddenDateRef = useRef(null);
  const textRef = useRef(null);

  // Focus the text field when autoFocus turns on. Using an effect (not the
  // bare autoFocus attribute) makes this fire reliably even when the input is
  // already mounted, e.g. a drawer that animates open without remounting.
  React.useEffect(() => {
    if (autoFocus && textRef.current) {
      textRef.current.focus();
    }
  }, [autoFocus]);

  // Text input change — parse and forward both values to parent
  const handleTextChange = (e) => {
    const raw = e.target.value;
    const parsed = parseDateInput(raw);
    onChange(raw, parsed ? parsed.iso : null);
  };

  // Calendar picker change — convert ISO → display, forward both
  const handlePickerChange = (e) => {
    const iso = e.target.value; // always ISO from native date input
    if (!iso) return;
    const display = isoToDisplay(iso);
    onChange(display, iso);
  };

  return (
    <div style={dateFieldStyles.wrapper}>
      <input
        ref={textRef}
        type="text"
        value={value}
        onChange={handleTextChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          ...dateFieldStyles.input,
          ...(error ? dateFieldStyles.inputError : {}),
          ...(inputStyle || {}),
        }}
      />
      <button
        type="button"
        style={dateFieldStyles.calendarBtn}
        onClick={() => hiddenDateRef.current?.showPicker?.()}
        disabled={disabled}
        tabIndex={-1}
        title="Open date picker"
      >
        <IconCalendarWeek size={16} stroke={1.5} style={{ color: 'var(--color-accent-navy)' }} />
      </button>

      {/* Hidden native date input — triggered by calendar button */}
      <input
        ref={hiddenDateRef}
        type="date"
        value={parseDateInput(value)?.iso || ''}
        onChange={handlePickerChange}
        disabled={disabled}
        style={dateFieldStyles.hiddenPicker}
        tabIndex={-1}
      />

      {error && (
        <div style={dateFieldStyles.errorText}>{error}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles — scoped to DateInputField
// ---------------------------------------------------------------------------

const dateFieldStyles = {
  wrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  input: {
    width: '100%',
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '10px',
    paddingRight: '32px', // room for calendar icon
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '13px',
    color: 'var(--color-text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: 'var(--color-bg-card)',
    fontFamily: 'var(--font-family-base)',
    transition: 'border-color 0.15s ease',
  },
  inputError: {
    borderColor: 'var(--color-severity-high)',
  },
  calendarBtn: {
    position: 'absolute',
    right: '8px',
    top: '6px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-muted)',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1,
  },
  hiddenPicker: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
    width: '1px',
    height: '1px',
    top: 0,
    left: 0,
  },
  errorText: {
    fontSize: '11px',
    color: 'var(--color-severity-high)',
    fontFamily: 'var(--font-family-base)',
  },
};

// ---------------------------------------------------------------------------
// Time input utility
//
// Accepted formats: 8:48 PM, 8:48pm, 20:48, 848pm, 0848
// Display format: H:MM AM/PM (e.g. 8:48 PM, 12:05 PM)
// Storage format: HH:MM (24-hour, for API)
// ---------------------------------------------------------------------------

export function parseTimeInput(str) {
  if (!str || !str.trim()) return null;
  const s = str.trim().toLowerCase().replace(/\s+/g, '');

  let hours, minutes, isPM, isAM;

  // Match formats: 8:48pm, 8:48 pm, 848pm, 848 pm
  const meridianMatch = s.match(/^(\d{1,2}):?(\d{2})(am|pm)$/);
  if (meridianMatch) {
    hours   = parseInt(meridianMatch[1], 10);
    minutes = parseInt(meridianMatch[2], 10);
    isPM    = meridianMatch[3] === 'pm';
    isAM    = meridianMatch[3] === 'am';

    if (isPM && hours !== 12) hours += 12;
    if (isAM && hours === 12) hours = 0;
  } else {
    // Match 24-hour formats: 20:48, 2048, 0848
    const h24Match = s.match(/^(\d{1,2}):?(\d{2})$/);
    if (!h24Match) return null;
    hours   = parseInt(h24Match[1], 10);
    minutes = parseInt(h24Match[2], 10);
  }

  if (isNaN(hours) || isNaN(minutes)) return null;
  if (hours < 0 || hours > 23)        return null;
  if (minutes < 0 || minutes > 59)    return null;

  // Storage format HH:MM (24-hour)
  const storage = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

  // Display format H:MM AM/PM
  const displayHour = hours % 12 || 12;
  const ampm        = hours < 12 ? 'AM' : 'PM';
  const display     = `${displayHour}:${String(minutes).padStart(2, '0')} ${ampm}`;

  return { storage, display };
}

export function storageToTimeDisplay(storageStr) {
  if (!storageStr) return '';
  return parseTimeInput(storageStr)?.display || storageStr;
}

export function isValidTimeInput(str) {
  return parseTimeInput(str) !== null;
}